import { WorkflowFormQuestion } from './types'
import { buildWorkflowByIdMap, parseWorkflowContent } from './parser'

/**
 * Get the ID of the first question to display: follows the trigger of the first
 * section node (e.g. section 100 -> W.Q.100.3) instead of blindly taking the
 * first question declared in the JSON.
 */
export const getFirstQuestionId = (
    workflowQuestions: WorkflowFormQuestion[],
    rawWorkflowContent: any[] | null,
): string | null => {
    if (workflowQuestions.length === 0) return null
    const parsed = parseWorkflowContent(rawWorkflowContent)
    const byId = buildWorkflowByIdMap(rawWorkflowContent)
    const firstSection = parsed.find((step) => step?.exec?.name === 'section')
    const visited = new Set<string>()
    let nodeId: string | null = typeof firstSection?.trigger === 'string' ? firstSection.trigger : null
    while (nodeId && !visited.has(nodeId)) {
        visited.add(nodeId)
        if (workflowQuestions.some((q) => q.id === nodeId)) return nodeId
        const node = byId.get(nodeId)
        nodeId = typeof node?.trigger === 'string' ? node.trigger : null
    }
    return workflowQuestions[0].id
}

/**
 * Find the next question ID given the current question and the selected choice ID.
 * It follows the trigger defined on the chosen answer option.
 *
 * @param currentQuestionId - The ID of the current question (e.g. "W.Q.100.1")
 * @param selectedChoiceId - The ID of the user-selected choice (e.g. "W.A.100.1")
 * @param workflowQuestions - The parsed workflow questions array
 * @param rawWorkflowContent - The raw workflow JSON content (for full byId map)
 * @returns The next question ID, or null if no trigger found
 */
export const getNextQuestionId = (
    currentQuestionId: string,
    selectedChoiceId: string,
    workflowQuestions: WorkflowFormQuestion[],
    rawWorkflowContent: any[] | null,
): string | null => {
    const currentQ = workflowQuestions.find((q) => q.id === currentQuestionId)
    // free-input answers (number, text...) are values, not choice ids: looking them up
    // in the workflow would match unrelated nodes (ex: value "100" matches section "100")
    const isFreeInput = !!currentQ && !currentQ.multiple && (currentQ.choices?.length ?? 0) === 0
    if (isFreeInput) {
        const freeInputNode = buildWorkflowByIdMap(rawWorkflowContent).get(currentQuestionId)
        return typeof freeInputNode?.trigger === 'string' ? freeInputNode.trigger : null
    }
    if (currentQ?.choices) {
        const selectedChoice = currentQ.choices.find((c) => c.id === selectedChoiceId)
        if (selectedChoice?.trigger) {
            return selectedChoice.trigger
        }
    }
    const byId = buildWorkflowByIdMap(rawWorkflowContent)
    const answerNode = byId.get(selectedChoiceId)
    if (answerNode && typeof answerNode.trigger === 'string') {
        return answerNode.trigger
    }
    if (currentQ?.multiple?.trigger) {
        return currentQ.multiple.trigger
    }
    const questionNode = byId.get(currentQuestionId)
    if (questionNode && typeof questionNode.trigger === 'string') {
        return questionNode.trigger
    }
    return null
}

/**
 * Get the next question IDs for auto nodes. If a question is auto-triggered,
 * it might have a trigger to the next question.
 */
export const getAutoNextQuestionId = (autoQuestionId: string, rawWorkflowContent: any[] | null): string | null => {
    const byId = buildWorkflowByIdMap(rawWorkflowContent)
    const node = byId.get(autoQuestionId)
    if (node && typeof node.trigger === 'string') {
        return node.trigger
    }
    return null
}

/**
 * Get the trigger value from a raw answer/choice node.
 */
export const getTriggerFromRawNode = (nodeId: string, rawWorkflowContent: any[] | null): string | null => {
    const byId = buildWorkflowByIdMap(rawWorkflowContent)
    const node = byId.get(nodeId)
    if (node && typeof node.trigger === 'string') {
        return node.trigger
    }
    return null
}

/**
 * Build the full workflow path by traversing the trigger graph,
 * starting from the first question and following triggers based on committed answers.
 * This is used to calculate progress and determine the current step position.
 *
 * @param workflowQuestions - Parsed workflow questions
 * @param committedAnswersByQuestionId - Map of questionId -> answer values
 * @param rawWorkflowContent - Raw workflow JSON for trigger lookups
 * @returns Ordered array of question IDs in the traversal path
 */
export const getWorkflowPath = (
    workflowQuestions: WorkflowFormQuestion[],
    committedAnswersByQuestionId: Map<string, any[]>,
    rawWorkflowContent: any[] | null,
): string[] => {
    if (workflowQuestions.length === 0) return []

    const path: string[] = []
    const visited = new Set<string>()
    let currentId: string | null = getFirstQuestionId(workflowQuestions, rawWorkflowContent)
    while (currentId && !visited.has(currentId)) {
        visited.add(currentId)
        path.push(currentId)
        const question = workflowQuestions.find((q) => q.id === currentId)
        if (question?.auto) {
            const autoNext = getAutoNextQuestionId(currentId, rawWorkflowContent)
            if (autoNext && !visited.has(autoNext)) {
                currentId = autoNext
                continue
            }
            break
        }
        const committedAnswer = committedAnswersByQuestionId.get(currentId)
        if (!committedAnswer || committedAnswer.length === 0) {
            break
        }
        const answerValue = committedAnswer[0]
        let selectedChoiceId: string | null = null
        if (typeof answerValue === 'string') {
            selectedChoiceId = answerValue
        } else if (typeof answerValue === 'object' && answerValue?.id) {
            selectedChoiceId = answerValue.id
        }
        let nextId: string | null = null
        if (selectedChoiceId) {
            nextId = getNextQuestionId(currentId, selectedChoiceId, workflowQuestions, rawWorkflowContent)
        }
        if (!nextId) {
            const rawNode = rawWorkflowContent?.find((n: any) => n.id === currentId)
            if (rawNode && typeof rawNode.trigger === 'string') {
                nextId = rawNode.trigger
            }
        }
        if (!nextId) {
            const currentIdx = workflowQuestions.findIndex((q) => q.id === currentId)
            const fallbackNext = workflowQuestions[currentIdx + 1]
            if (fallbackNext) {
                nextId = fallbackNext.id ?? null
            }
        }
        if (!nextId || visited.has(nextId)) {
            break
        }
        currentId = nextId
    }
    return path
}

/**
 * Find the first unanswered question by traversing the trigger graph.
 * This replaces the linear `getFirstUnansweredQuestion` with a path-aware version.
 *
 * @returns The first unanswered WorkflowFormQuestion, or the last question if all answered
 */
export const getFirstUnansweredQuestionId = (
    workflowQuestions: WorkflowFormQuestion[],
    committedAnswersByQuestionId: Map<string, any[]>,
    rawWorkflowContent: any[] | null,
): string | null => {
    if (workflowQuestions.length === 0) return null
    const path = getWorkflowPath(workflowQuestions, committedAnswersByQuestionId, rawWorkflowContent)

    for (const qId of path) {
        const answer = committedAnswersByQuestionId.get(qId)
        if (!answer || answer.length === 0) {
            return qId
        }
    }
    const lastId = path[path.length - 1]
    if (lastId) {
        const autoNext = getAutoNextQuestionId(lastId, rawWorkflowContent)
        if (autoNext) {
            const nextQ = workflowQuestions.find((q) => q.id === autoNext)
            if (nextQ?.id) return nextQ.id
        }
    }
    return lastId
}

/**
 * Get workflow progress based on the trigger-based path.
 * This ensures the progress bar reflects the actual path the user is on.
 */
export const getWorkflowProgressFromPath = (
    workflowQuestions: WorkflowFormQuestion[],
    committedAnswersByQuestionId: Map<string, any[]>,
    rawWorkflowContent: any[] | null,
    currentQuestionId: string | null,
): { currentStep: number; totalSteps: number; completedSteps: number; isComplete: boolean } => {
    if (workflowQuestions.length === 0) {
        return { currentStep: 0, totalSteps: 0, completedSteps: 0, isComplete: false }
    }
    const path = getWorkflowPath(workflowQuestions, committedAnswersByQuestionId, rawWorkflowContent)
    const interactiveQuestions = workflowQuestions.filter((q) => !q.auto)
    const totalSteps = Math.max(interactiveQuestions.length, 1)
    const completedSteps = path.filter((qId) => {
        const q = workflowQuestions.find((item) => item.id === qId)
        if (q?.auto) return false
        const answer = committedAnswersByQuestionId.get(qId)
        return answer && answer.length > 0
    }).length
    let currentStep = completedSteps + 1
    if (currentQuestionId) {
        const interactivePath = path.filter((qId) => {
            const q = workflowQuestions.find((item) => item.id === qId)
            return !q?.auto
        })
        const idx = interactivePath.indexOf(currentQuestionId)
        if (idx !== -1) {
            currentStep = idx + 1
        }
    }
    currentStep = Math.min(Math.max(currentStep, 1), totalSteps)
    return {
        currentStep,
        totalSteps,
        completedSteps,
        isComplete: completedSteps >= totalSteps,
    }
}
